import React, { useEffect, useState } from "react";
import {
  AuditOutlined,
  ContainerOutlined,
  LineChartOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  OrderedListOutlined,
  RobotOutlined,
  SettingOutlined,
  SnippetsOutlined,
  TabletOutlined,
} from "@ant-design/icons";
import { Button, Layout, Menu, theme } from "antd";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Profile from "../components/other/profile";
import { useAdmin } from "context/adminContext";
import { bg_button, bg_text } from "constant/css";
import MerchantHelper from "components/other/merchantHelper";

const { Header, Content, Sider } = Layout;

const AdminLayout = () => {
  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const { accounts } = useAdmin();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [openKeys, setOpenKeys] = useState(["bankTransactionLogs"]);
  const iconSider = collapsed && !isMobile;

  const accountMenuChildren =
    accounts?.length > 0
      ? accounts.map((account) => ({
          iconf: (props) => <ContainerOutlined {...props} />,
          key: `bankTransactionLog/${account.accountNumber}`,
          label: (
            <span className="text-xs">
              {account.accountNumber}
              <span className="text-gray-300"> ({account.type})</span>
            </span>
          ),
        }))
      : [
          {
            key: "bankTransactionLogs-empty",
            label: (
              <span className="text-xs text-gray-400">Данс ачааллаж байна...</span>
            ),
            disabled: true,
          },
        ];

  const items = [
    {
      iconf: (props) => <LineChartOutlined {...props} />,
      icon: <LineChartOutlined />,
      key: "adminDashboard",
      label: "Хянах самбар",
    },
    {
      iconf: (props) => <ContainerOutlined {...props} />,
      icon: <ContainerOutlined />,
      key: "bankTransactionLogs",
      label: "Дансны хуулга",
      popupClassName: "admin-submenu-popup",
      children: accountMenuChildren,
    },
    {
      iconf: (props) => <SnippetsOutlined {...props} />,
      icon: <SnippetsOutlined />,
      key: "adminInvoice",
      label: "Гүйлгээ",
    },
    {
      iconf: (props) => <SettingOutlined {...props} />,
      icon: <SettingOutlined />,
      key: "adminMerchants",
      label: "Мерчантууд",
    },
    {
      iconf: (props) => <OrderedListOutlined {...props} />,
      icon: <OrderedListOutlined />,
      key: "reference",
      label: "Мэдээллын сан",
      children: [
        {
          iconf: (props) => <RobotOutlined {...props} />,
          key: "functionCall",
          label: (
            <div className="flex items-center relative gap-2">
              <span>AI асуулт</span>
              <span
                className={`text-[8px] flex justify-center px-2 ${bg_button} absolute h-[20px] items-center text-center right-2 font-semibold text-white animate-pulse rounded-md shadow-md`}
              >
                шинэ
              </span>
            </div>
          ),
        },
        {
          iconf: (props) => <OrderedListOutlined {...props} />,
          key: "productType",
          label: "Бүтээгдэхүүн",
        },
        {
          iconf: (props) => <OrderedListOutlined {...props} />,
          key: "bankCode",
          label: "Банкны код",
        },
        {
          iconf: (props) => <OrderedListOutlined {...props} />,
          key: "vatFreeGoods",
          label: "vatFreeGoods",
        },
        {
          iconf: (props) => <OrderedListOutlined {...props} />,
          key: "vatZeroGoods",
          label: "vatZeroGoods",
        },
      ],
    },
    {
      iconf: (props) => <AuditOutlined {...props} />,
      icon: <AuditOutlined />,
      key: "reconciliation",
      label: "Reconciliation",
    },
    {
      iconf: (props) => <TabletOutlined {...props} />,
      icon: <TabletOutlined />,
      key: "pos",
      label: "Пос",
    },
  ];

  const [selected, setSelected] = useState(items[0]);
  const selectedKeys = [selected?.key || items[0].key];

  useEffect(() => {
    const findItemByKey = (key, list) => {
      for (const item of list) {
        if (item.key === key) return item;
        if (item.children) {
          const found = findItemByKey(key, item.children);
          if (found) return found;
        }
      }
      return null;
    };

    let currentPath = location.pathname.replace(/^\//, "");
    if (currentPath.startsWith("faultyBankTransactionLog/")) {
      currentPath = currentPath.replace(
        "faultyBankTransactionLog/",
        "bankTransactionLog/"
      );
    }
    const matchedKey = currentPath.startsWith("adminMerchants/")
      ? "adminMerchants"
      : currentPath;
    const currentItem = findItemByKey(matchedKey, items);
    if (currentItem) setSelected(currentItem);
    if (matchedKey.startsWith("bankTransactionLog")) {
      setOpenKeys((keys) =>
        keys.includes("bankTransactionLogs")
          ? keys
          : [...keys, "bankTransactionLogs"]
      );
    }
  }, [location.pathname, accounts]);

  const setAndNavigate = (e) => {
    const findItem = (key, list) => {
      for (const item of list) {
        if (item.key === key) return item;
        if (item.children) {
          const found = findItem(key, item.children);
          if (found) return found;
        }
      }
      return null;
    };
    if (e.key === "bankTransactionLogs" || e.key === "reference") return;
    const item = findItem(e.key, items);
    if (item) setSelected(item);
    navigate(e.key);
    if (isMobile) setCollapsed(true);
  };

  return (
    <Layout style={{ height: "100vh" }} hasSider>
      {/* Mobile overlay */}
      {isMobile && !collapsed && (
        <div
          onClick={() => setCollapsed(true)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 998,
          }}
        />
      )}

      <Sider
        width={220}
        collapsible
        collapsed={collapsed}
        collapsedWidth={isMobile ? 0 : 64}
        onCollapse={setCollapsed}
        breakpoint="lg"
        onBreakpoint={(broken) => {
          setIsMobile(broken);
          setCollapsed(broken);
        }}
        trigger={null}
        className="admin-sider"
        style={{
          background: colorBgContainer,
          overflow: "visible",
          height: "100vh",
          position: isMobile ? "fixed" : "sticky",
          insetInlineStart: 0,
          top: 0,
          bottom: 0,
          zIndex: isMobile ? 999 : 1,
          boxShadow:
            isMobile && !collapsed ? "2px 0 8px rgba(0,0,0,0.18)" : "none",
          transition: "all 0.2s",
        }}
      >
        {/* Logo */}
        <div
          style={{
            padding: collapsed ? "16px 8px" : "16px 20px",
            display: "flex",
            flexDirection: "column",
            alignItems: collapsed ? "center" : "flex-start",
            overflow: "hidden",
            minHeight: 72,
            justifyContent: "center",
            transition: "padding 0.2s",
          }}
        >
          {collapsed ? (
            <img
              src="/smartbilling.png"
              width={32}
              height={32}
              className="rounded-xl"
              style={{ flexShrink: 0 }}
            />
          ) : (
            <>
              <span
                className={`${bg_text} font-black`}
                style={{ fontSize: 14, whiteSpace: "nowrap" }}
              >
                Смарт Биллинг Админ
              </span>
              <span
                className={`${bg_text}`}
                style={{ fontSize: 11, whiteSpace: "nowrap", opacity: 0.7 }}
              >
                Rapid, Reliable, Rewarding
              </span>
            </>
          )}
        </div>

        <div className="admin-sider-menu">
          <Menu
            theme="light"
            mode="inline"
            inlineCollapsed={iconSider}
            selectedKeys={selectedKeys}
            openKeys={iconSider ? undefined : openKeys}
            onOpenChange={setOpenKeys}
            onClick={setAndNavigate}
            items={items}
            triggerSubMenuAction="click"
            getPopupContainer={() => document.body}
            style={{ borderInlineEnd: "none" }}
          />
        </div>

        {!collapsed && <Profile />}
      </Sider>

      <Layout
        style={{
          display: "flex",
          flexDirection: "column",
          marginLeft: isMobile ? 0 : undefined,
        }}
      >
        <Header
          style={{
            background: colorBgContainer,
            padding: "0 16px",
            display: "flex",
            alignItems: "center",
            height: 56,
            justifyContent: "space-between",
            borderBottom: "1px solid #f0f0f0",
            position: "sticky",
            top: 0,
            zIndex: 100,
            gap: 8,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            <Button
              type="text"
              icon={
                collapsed ? (
                  <MenuUnfoldOutlined style={{ fontSize: 18 }} />
                ) : (
                  <MenuFoldOutlined style={{ fontSize: 18 }} />
                )
              }
              onClick={() => setCollapsed(!collapsed)}
              style={{ flexShrink: 0 }}
            />
            {selected.iconf({ style: { fontSize: 22, color: "#32B7C2", flexShrink: 0 } })}
            {!selected.type && (
              <h2
                style={{
                  margin: 0,
                  fontSize: 16,
                  color: "#32B7C2",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {selected.label}
              </h2>
            )}
            {selected.type === "merchant" && (
              <h2
                style={{
                  margin: 0,
                  fontSize: 16,
                  color: selected.color,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {selected.slogan}
              </h2>
            )}
          </div>
        </Header>

        <div className="absolute bottom-10 right-10" style={{ zIndex: 99 }}>
          <MerchantHelper />
        </div>

        <Content
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "5px 12px",
            background: colorBgContainer,
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default AdminLayout;
